'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import type { CopVideoStream } from './types';

/**
 * How often the stream registry is re-asked while a consumer is mounted.
 * Settled at 15 s in the feature spec: fast enough that the FEED health dot is
 * trustworthy, slow enough that it costs nothing on a busy COP.
 */
export const VIDEO_STREAM_POLL_MS = 15_000;

/** No async operation may hang forever — CLAUDE.md's reliability standard. */
export const REQUEST_TIMEOUT_MS = 10_000;

export interface DroneVideoStreamResult {
  /** Every camera registered for this aircraft, stably ordered by the API.
   *  An airframe can carry more than one - the SkyPulse has a nose camera and a
   *  down camera, and the down one is what a tray approach is watched on. */
  streams: CopVideoStream[];
  /** The first camera, for callers that only need to know whether ANY feed
   *  exists (the FEED health dot). Prefer `streams` when rendering. */
  stream: CopVideoStream | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface StreamState {
  streams: CopVideoStream[];
  loading: boolean;
  error: string | null;
}

const IDLE: StreamState = { streams: [], loading: false, error: null };

/**
 * Stream-registry health for one aircraft.
 *
 * Lifted out of the old CopVideoDock so the FEED segment and the video window
 * read the same endpoint the same way instead of each inventing a fetch. It
 * polls only while mounted — both consumers mount only while the operator is
 * looking at them — and stops asking entirely while the tab is hidden, then
 * asks once on the way back so the dot is never stale at the moment it is read.
 *
 * A response is written to state only if it belongs to the drone currently
 * asked about and the hook is still mounted: switching aircraft mid-flight must
 * never paint the previous aircraft's stream under the new one's callsign.
 */
export function useDroneVideoStream(droneId: string): DroneVideoStreamResult {
  const [state, setState] = useState<StreamState>(IDLE);

  // Every request stamps the generation it belongs to. Bumping the generation
  // (drone change, unmount) retires everything already in flight.
  const generation = useRef(0);
  const mounted = useRef(true);
  const inFlight = useRef<AbortController | null>(null);

  /**
   * `reset` is the new-aircraft case: clear the previous drone's answer, show
   * the skeleton, ask again. A background poll does none of that — it leaves
   * the last answer standing until a new one arrives, so the segment never
   * blinks and, more importantly, a red dot never erases itself on a timer.
   */
  const runFetch = useCallback((id: string, reset = false) => {
    const mine = ++generation.current;
    inFlight.current?.abort();
    const controller = new AbortController();
    inFlight.current = controller;
    // Nothing may hang forever: a socket the API accepts and never answers
    // would otherwise pin `loading` on and leak one connection per poll.
    const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

    if (reset) setState({ streams: [], loading: true, error: null });

    api
      .get<{ data: CopVideoStream[] }>(`/api/video/streams?drone_id=${encodeURIComponent(id)}`, {
        signal: controller.signal,
      })
      .then((r) => {
        if (!mounted.current || mine !== generation.current) return;
        setState({ streams: r.data ?? [], loading: false, error: null });
      })
      .catch((err: unknown) => {
        if (!mounted.current || mine !== generation.current) return;
        // Retired requests abort by design; that is not an operator-facing fault.
        if (controller.signal.aborted && mine !== generation.current) return;
        console.warn('[cop] video stream health poll failed', { droneId: id, err });
        // Distinct from "no stream registered": we failed to ask, which is not
        // proof the camera is down. FEED renders the two differently. The last
        // known stream is kept — a failed refresh must not tear down a playing
        // picture, and stale-but-labelled beats blank.
        setState((prev) => ({
          streams: prev.streams,
          loading: false,
          error: 'Could not read stream health',
        }));
      })
      .finally(() => {
        window.clearTimeout(timeout);
        if (inFlight.current === controller) inFlight.current = null;
      });
  }, []);

  useEffect(() => {
    mounted.current = true;
    // A new aircraft starts from nothing — holding the last one's rows while
    // the first fetch runs would be the wrong answer under the right callsign.
    runFetch(droneId, true);

    const poll = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return;
      runFetch(droneId);
    }, VIDEO_STREAM_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'visible') runFetch(droneId);
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      mounted.current = false;
      generation.current += 1;
      inFlight.current?.abort();
      inFlight.current = null;
      window.clearInterval(poll);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [droneId, runFetch]);

  const refetch = useCallback(() => runFetch(droneId), [droneId, runFetch]);

  return {
    streams: state.streams,
    stream: state.streams[0] ?? null,
    loading: state.loading,
    error: state.error,
    refetch,
  };
}
